import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable, Output } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router } from '@angular/router';
import jwt_decode from 'jwt-decode';
import { SignoutResponse, User, UserManager, UserManagerSettings } from 'oidc-client';
import { environment } from '@app/environments/environment';
import { ApiService } from '@app/services/api.service';
import { TourGuideService } from '@app/services/tour-guide.service';
import featureData from '@app/assets/page_feature.json';
import { CommonFunctionalitiesService } from '../common-functionalities.service';
import menuData from '@app/assets/menu.json';

/**
 * @description This class used for Authentication
 * @export
 * @class AuthService
 */
@Injectable({
    providedIn: 'root'
})

export class AuthService {
    public readonly manager :UserManager = new UserManager(getClientSettings());
    public user: User | any;
    public roles: string[];
    private canAccess: string[] = [];
    public name: string;
    public username: string;
    public gmId: string;
    public gmIn: number;
    public localSt: any;
    public permissions: any = {}
    @Output() getLoggedInName: EventEmitter<any> = new EventEmitter();
    @Output() getAccessToken: EventEmitter<any> = new EventEmitter();
    // Use RxJS BehaviorSubject for user data stream (replays latest value to new' subscribers)
    private getUserDataSubject = new BehaviorSubject<User | null>(null);
    public getUserData$: Observable<User | null> = this.getUserDataSubject.asObservable();
    
    prefix = 'oidc.user:';
    loggedIn = false;
    newUser = false;

    proxyLogin = false;
    private isAuthenticated = true;

    public authApis = this.apiService.AUTH_APIS
    public userApis = this.apiService.USER
    public authUrl = this.apiService.KPID_URL + this.authApis.AUTH;
    public userUrl = this.apiService.KPID_URL + this.userApis.USER;

    /**
     * Creates an instance of AuthService.
     * @param {Router} router
     * @memberof AuthService
     */
    constructor(
        private readonly router: Router,
        private httpClient: HttpClient,
        private apiService: ApiService,
        private TourGuideService: TourGuideService,
        private cf: CommonFunctionalitiesService
    ) {
        this.manager.getUser().then((user: any) => {
            this.user = user;
            if (this.user) {
                this.roles = this.user?.profile?.roles && this.user?.profile?.roles.length > 0 ? this.user?.profile?.roles : [];
                const decodedUser: any = jwt_decode(<string>this.user?.id_token);
                this.username = decodedUser.preferred_username.substr(0, decodedUser.preferred_username.indexOf("@"));
                this.gmId = this.user.profile.uid
                this.getUserDataSubject.next(this.user);
            }
            this.checkPermissions()
        });

        window.addEventListener('storage', function (e) {
            const key = 'oidc.user:' + `${environment.auth.issuer}` + ':' + `${environment.auth.client_id}`;
            sessionStorage.removeItem(key);
        });

        this.cf.prodTour$.subscribe((res: any) => {
            if (res == true) {
                this.proxyLogin = true;
            } else if (res == false) {
                this.proxyLogin = false;
            }
        })
    }
 
    // use for autheticate by auth gaurd
    // return boolean value
    isLoggedIn(): boolean {
        return this.proxyLogin || (this.user != null && !this.user.expired && this.isAuthenticated);
    }

    /**
     * @description add token into the local storage
     * @returns {string}
     * @memberof AuthService
     */
    getAuthorizationHeaderValue(): string {
        let token = '';
        if (this.user?.id_token !== undefined) {
            token = `Bearer ${this.user.id_token}`;
        }
        return token;
    }


    /**
     * @description Authenticate the user
     * @returns {Promise<void>}
     * @memberof AuthService
     */
    startAuthentication(): Promise<void> {
        return this.manager.signinRedirect();
    }

    /**
     * @description starts logout
     * @memberof AuthService
     */
    startLogout(): void {
        this.manager.signoutRedirect().then((res: any) => {
            this.completeLogout().then(resp => {
                this.manager.clearStaleState();
                this.manager.removeUser();
                this.manager.revokeAccessToken();
                const key = `${this.prefix}${environment.auth.issuer}` + ':' + `${environment.auth.client_id}`;
                sessionStorage.removeItem(key);
                localStorage.clear();
                sessionStorage.clear();
                this.isAuthenticated = false;
                this.router.navigate(['/logout']);
            });
        });
    }

    /**
     * @description Used to getRoles
     * @returns {String[]}
     * @memberof AuthService
     */
    getRoles(): string[] {
        return this.roles;
    }

    /**
     * @description Checks whether user has role
     * @param {string} role
     * @returns {boolean}
     * @memberof AuthService
     */
    hasRole(role: string): boolean {
        return this.roles instanceof Array && this.roles.indexOf(role) !== -1;
    }

    /**
     * @description This function is used to get User name
     * @returns {string}
     * @memberof AuthService
     */
    getUserName(): string {
        return this.username;
    }

    /**
     * @description This function is called on complete authentication through auth-callback
     * @returns {Promise<void>}
     * @memberof AuthService
     */
    completeAuthentication(): Promise<any> {
        
        return this.manager.signinRedirectCallback().then((user: any) => {
            this.user = user;
            this.getUserDataSubject.next(this.user);
            const decodedUser: any = jwt_decode(<string>this.user?.id_token);

            this.name = this.user?.profile?.givenName ? this.user?.profile?.givenName : "Unknown";
            this.username = this.user?.profile?.name ? this.user?.profile?.name : "Unknown";

            this.gmId = this.user.profile.uid;
            this.gmIn = this.user.profile.gmuniqueidentifier;

            this.getLoggedInName.emit(this.name);
            this.getAccessToken.emit(this.user?.access_token);

            const email: string = <string>this.user?.profile['mail'];

            this.roles = this.user?.profile?.roles ? this.user?.profile?.roles : [];

            //TODO: Handle case when roles are empty
            if (this.roles.length >= 0) {
                this.httpClient.post(this.userUrl + this.userApis.AUTH_ADD_USER, {
                    gmIn: this.gmIn,
                    gmId: this.gmId,
                    usrNm: this.username,
                    email,
                    roles: this.roles
                }).subscribe((resp: any) => {
                    this.newUser = resp.data.dbResp.newUser;
                    let accessFeatures = resp.data.dbResp.accsFeat;
                    if (this.newUser) {
                        this.TourGuideService.productTour();
                    }
                    this.filterResponse(accessFeatures);
                },
                    (err: any) => {
                        console.log(err);
                    })
            }
        }).catch((e: any) => {
            console.error("Error in add user: " + JSON.stringify(e));
        });
    }

    /**
     * Method to filter response for complete authentication
     * @param res
     * @memberof AuthService
     */
    filterResponse(res: any) {
        const roles = this.getRoles();
        this.setFeatureAccess(res);

        if (roles && roles.length >= 0) {
            this.updateRoutesBasedOnRes();
        } else {
            this.router.navigateByUrl('/app-auth-callback');
        }
    }

    /**
     * @description Handled routes based on resp and role based access
     * @memberof AuthService
     */
    updateRoutesBasedOnRes() {
        const str = String(sessionStorage.getItem('usrEnteredURL')) || '';
        
        this.router.config.forEach(routes => {
            if ("/"+routes.path === str?.split("?")[0] && str?.substring(1) !== '') {
                this.router.navigateByUrl(str);
            }
        });

        if (str.substring(1) === '' || !sessionStorage.getItem('usrEnteredURL')) {
            // Load navigation as per the menu access for the user
            const pages: any = sessionStorage.getItem('pages');

            let MenuePages = pages?.split(',') || []

            const nameOrdMap = menuData.reduce((map: any, obj: any, index) => {
                map[obj.text] = index;
                return map
            }, {})

            MenuePages = MenuePages.sort((a: any, b: any) => nameOrdMap[a] - nameOrdMap[b])
            let mainmenu: any = menuData.filter((obj: any) => MenuePages.includes(obj.text))

            /**
             * Parent router and child router variables is to find the route according to 
             * window location pathname and search the same in menuData json
             * if the path name matches then the navigation will be done directly to that pathname
             */
            let parentRouter = mainmenu.find((router: any) => router.routerLink == window.location.pathname);
            let childRouters = mainmenu.reduce((prev: any, product: any) => prev || product?.children?.find((item: any) => item.routerLink === window.location.pathname), undefined)

            if (parentRouter || childRouters) {
                return;
            }
            this.router.navigateByUrl(mainmenu[0]?.routerLink || '/');
        }
    }

    /**
     * @description This function used to logout
     * @returns {Promise<SignoutResponse>}
     * @memberof AuthService
     */
    completeLogout(): Promise<SignoutResponse> {
        this.user = null;
        this.getUserDataSubject.next(null);
        return this.manager.signoutRedirectCallback();
    }

    /**
     * to store feature in session storage
     * @param pages
     * @memberof AuthService
     */
    setFeatureAccess(pageAndfeat: { page: string, feat: string[] }[]): void {
        let pages = pageAndfeat.map(item => item.page);
        let features = pageAndfeat.map(item => item.feat);

        if (features.some((feat: string | string[]) => feat.includes('edit-access') || feat.includes('view-tool') || feat.includes('view-link'))) {
            pages.push('Admin')
        }

        // If dashboard does not contain any features then remove the dashboard page
        features.some((feat: string | string[]) => feat.includes('summary') || feat.includes('distribution') || feat.includes('error-diagnostics') || feat.includes('licenses') || feat.includes('kpidashboard')) ? '' : pages = pages.filter(page => page !== 'Dashboard')
        this.canAccess = pages;
        
        sessionStorage.setItem('pages', pages.toString());
        sessionStorage.setItem('features', features.toString());
        this.checkPermissions()
    }

    /**
     * @description to get the pages access
     * @returns
     * @memberof AuthService
     */
    getFeatureAccess(): string[] {
        return this.canAccess;
    }

    /**
     * @description to get feature access on permission
     * @param name
     * @returns
     * @memberof AuthService
     */
    hasPagePermission(name: string): boolean {
        if (this.proxyLogin) {
            return true;
        }

        const pages = sessionStorage.getItem('pages');
        return pages != null && pages.split(',').indexOf(name) > -1;
    }

    hasFeaturePermission(name: string): boolean {
        if (this.proxyLogin) {
            return true;
        }
        const feat = sessionStorage.getItem('features')
        return feat != null && feat.split(',').indexOf(name) > -1;
    }

    checkPermissions() {
        const config = JSON.parse(JSON.stringify(featureData)); // Parse the JSON file
        for (let i = 0; i < config.length; i++) {
            for (let j = 0; j < config[i].feature.length; j++) {
                const feature = config[i].feature[j];
                this.permissions[`${feature}`] = false; // Assign a boolean value to each feature
            }
        }

        for (const obj in this.permissions) {
            if (this.hasFeaturePermission(obj)) {
                this.permissions[obj] = true;
            }
        }
    }

}


/**
 * @description This function is used to get client settings
 * @export
 * @returns {UserManagerSettings}
 * @memberof AuthService
 */
export function getClientSettings(): UserManagerSettings {
    return {
        authority: `${environment.auth.authority}`,
        client_id: `${environment.auth.client_id}`,
        redirect_uri: `${environment.auth.redirect_uri}`,
        post_logout_redirect_uri: `${environment.auth.post_logout_redirect_uri}`,
        response_type: 'code',
        scope: 'openid profile',
        // scope: ['openid', `${environment.auth.scopes}` ].join(' '),
        filterProtocolClaims: true,
        revokeAccessTokenOnSignout: true,
        automaticSilentRenew: true,
        loadUserInfo: false
    };
}